import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  ExitIcon,
  GearIcon,
  HomeIcon,
  ImageIcon,
  LayersIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PersonIcon,
  ReaderIcon,
  RowsIcon,
  TextAlignJustifyIcon,
  SunIcon,
  VideoIcon,
  ActivityLogIcon,
  TokensIcon,
  LightningBoltIcon,
} from '@radix-ui/react-icons';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { useTaskStore } from '../../stores/taskStore';
import { sseClient } from '../../lib/sseClient';
import { cn } from '../../lib/utils';
import NotificationBell from '../notification/NotificationBell';
import NotificationToast from '../notification/NotificationToast';
import TaskProgressIndicator from '../common/TaskProgressIndicator';
import { createCommandItems, createPrimaryNavItems, LayoutNavItem } from './navConfig';
import { getUserTasks } from '../../services/taskService';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  home: HomeIcon,
  video: VideoIcon,
  assets: LayersIcon,
  book: ReaderIcon,
  image: ImageIcon,
  tasks: RowsIcon,
  roles: PersonIcon,
  admin: GearIcon,
  profile: PersonIcon,
  activity: ActivityLogIcon,
  points: TokensIcon,
  recharge: LightningBoltIcon,
};

function NavIcon({ icon, className }: { icon?: string; className?: string }) {
  const Icon = icon ? ICONS[icon] : BellIcon;
  return <Icon className={className} />;
}

function isItemActive(pathname: string, item: LayoutNavItem) {
  if (item.path === '/') return pathname === '/';
  if (item.path === '/admin') return pathname === '/admin';
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function isTaskEventForUser(event: Record<string, unknown>, userId: number): boolean {
  const eventUserId = event.userId;
  return eventUserId !== undefined && String(eventUserId) === String(userId);
}

const Layout: React.FC = () => {
  const { user, logout, token } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const commandInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = useMemo(() => createPrimaryNavItems(user), [user]);
  const commandResults = useMemo(
    () => createCommandItems(user).filter((item) =>
      `${item.label} ${item.path}`.toLowerCase().includes(commandQuery.trim().toLowerCase())
    ),
    [commandQuery, user]
  );

  const openCommand = () => {
    setCommandOpen(true);
    setSelectedCommandIndex(0);
  };

  const closeCommand = () => {
    setCommandOpen(false);
    setCommandQuery('');
    setSelectedCommandIndex(0);
  };

  const navigateToCommandItem = (item: LayoutNavItem) => {
    navigate(item.path);
    closeCommand();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (commandOpen && !target.closest('[data-command-panel]')) {
        setCommandOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [commandOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (commandOpen) {
          closeCommand();
        } else {
          openCommand();
        }
      }
      if (e.key === 'Escape') {
        closeCommand();
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  useEffect(() => {
    if (commandOpen) {
      commandInputRef.current?.focus();
    }
  }, [commandOpen]);

  useEffect(() => {
    setCommandOpen(false);
    setCommandQuery('');
    setSelectedCommandIndex(0);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setSelectedCommandIndex(0);
  }, [commandQuery, commandResults.length]);

  useEffect(() => {
    if (!commandOpen && !mobileOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [commandOpen, mobileOpen]);

  useEffect(() => {
    if (!token || !user) {
      useTaskStore.getState().clearAll();
      return;
    }

    useTaskStore.getState().clearAll();

    const reconcileTasks = async () => {
      const requestedAt = Date.now();
      try {
        const result = await getUserTasks({ statusGroup: 'running', limit: 100 });
        useTaskStore.getState().reconcileActiveTasks(result.tasks.map((task) => ({
          taskId: task.id,
          type: task.type,
          status: task.status.toLowerCase(),
          progress: task.progress || 0,
          phase: '',
        })), requestedAt);
      } catch (error) {
        console.error('[SSE] Failed to reconcile active tasks:', error);
      }
    };

    const unsubscribe = sseClient.subscribe('task_lifecycle', (data) => {
      const { taskId, payload } = data;
      if (!taskId || !isTaskEventForUser(data, user.id)) return;
      const lifecyclePayload = (payload || {}) as Record<string, unknown>;
      const { status, progress } = lifecyclePayload;
      const metadata = (lifecyclePayload.metadata as Record<string, unknown>) || {};
      const type = typeof metadata.type === 'string' ? metadata.type : 'unknown';
      const phase = typeof metadata.phase === 'string' ? metadata.phase : '';
      const progressValue = Number(progress || 0);
      const taskStore = useTaskStore.getState();

      taskStore.applyLifecycleEvent({
        taskId: String(taskId),
        type,
        status: String(status || ''),
        progress: progressValue,
        phase,
        error: typeof lifecyclePayload.error === 'string' ? lifecyclePayload.error : undefined,
        timestamp: typeof data.timestamp === 'number' ? data.timestamp : Date.now(),
      });

    });

    sseClient.connect(token, () => {
      void reconcileTasks();
    });

    return () => {
      unsubscribe();
      sseClient.disconnect();
      useTaskStore.getState().clearAll();
    };
  }, [token, user]);

  const handleLogout = () => {
    void logout();
    navigate('/');
  };

  const handleCommandKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (commandResults.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedCommandIndex((current) => Math.min(current + 1, commandResults.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedCommandIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter' && commandResults[selectedCommandIndex]) {
      event.preventDefault();
      navigateToCommandItem(commandResults[selectedCommandIndex]);
    }
  };

  const renderSidebar = (collapsed = false) => (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border-default bg-surface-elevated transition-[width] duration-200',
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      <div className={cn(
        'flex items-center border-b border-border-default',
        collapsed ? 'h-24 flex-col justify-center gap-2 px-3' : 'h-16 justify-between gap-3 px-5'
      )}>
        <Link
          to="/"
          className={cn('flex min-w-0 items-center gap-3', collapsed && 'justify-center')}
          aria-label="CultureClick"
          title="CultureClick"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
            <VideoIcon className="h-5 w-5" />
          </span>
          {collapsed ? null : (
            <span className="truncate text-base font-bold text-content-primary">CultureClick</span>
          )}
        </Link>
        <button
          type="button"
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className={cn(
            'hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-hover hover:text-content-primary lg:flex',
            collapsed && 'border border-border-default bg-surface-card shadow-surface-sm'
          )}
          aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
          title={collapsed ? '展开侧边栏' : '收起侧边栏'}
        >
          <TextAlignJustifyIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="p-3">
        <button
          type="button"
          onClick={openCommand}
          className={cn(
            'flex h-10 w-full items-center rounded-lg border border-border-default bg-surface-card text-sm text-content-muted transition-colors hover:border-border-hover hover:text-content-primary',
            collapsed ? 'justify-center px-0' : 'gap-2 px-3'
          )}
          aria-label="搜索"
          title="搜索"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          {collapsed ? null : (
            <>
              <span className="flex-1 text-left">搜索</span>
              <kbd className="rounded-md bg-surface-tertiary px-1.5 py-0.5 text-[10px] text-content-muted">Ctrl K</kbd>
            </>
          )}
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {navItems.map((item) => {
          const active = isItemActive(location.pathname, item);
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-label={item.label}
              title={collapsed ? item.label : undefined}
              className={cn(
                'flex h-10 items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                active
                  ? 'bg-primary text-white shadow-surface-sm'
                  : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary'
              )}
            >
              <NavIcon icon={item.icon} className="h-4 w-4 shrink-0" />
              {collapsed ? null : <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="space-y-3 border-t border-border-default p-3">
        <div className={cn('flex items-center gap-2', collapsed && 'flex-col')}>
          <TaskProgressIndicator compact={collapsed} />
          {user ? <NotificationBell /> : null}
          <button
            type="button"
            onClick={toggleTheme}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-hover hover:text-content-primary"
            aria-label="切换主题"
            title="切换主题"
          >
            {theme === 'dark' ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
          </button>
        </div>

        {user ? (
          <div className={cn('rounded-lg border border-border-default bg-surface-card p-2', collapsed && 'flex flex-col items-center')}>
            <Link
              to="/profile"
              className={cn(
                'flex items-center rounded-md hover:bg-surface-hover',
                collapsed ? 'justify-center p-1.5' : 'gap-3 px-2 py-2'
              )}
              aria-label="个人资料"
              title={collapsed ? user.username : undefined}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
                {user.username.charAt(0).toUpperCase()}
              </span>
              {collapsed ? null : (
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-content-primary">{user.username}</span>
                  <span className="block truncate text-xs text-content-muted">{user.email}</span>
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className={cn(
                'mt-1 flex h-9 w-full items-center rounded-md text-sm text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary',
                collapsed ? 'justify-center px-0' : 'gap-2 px-2'
              )}
              aria-label="退出登录"
              title="退出登录"
            >
              <ExitIcon className="h-4 w-4" />
              {collapsed ? null : '退出登录'}
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="flex h-10 items-center justify-center rounded-lg bg-primary px-3 text-sm font-semibold text-white hover:bg-primary/90"
            aria-label="登录"
            title="登录"
          >
            {collapsed ? <PersonIcon className="h-4 w-4" /> : '登录'}
          </Link>
        )}
      </div>
    </aside>
  );

  return (
    <div className="h-screen overflow-hidden bg-surface-primary text-content-primary">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-lg focus:bg-surface-elevated focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-content-primary focus:shadow-surface-lg focus:outline-none focus:ring-2 focus:ring-primary"
      >
        跳转到主内容
      </a>
      <NotificationToast />

      <div className="fixed inset-y-0 left-0 z-[180] hidden lg:block">{renderSidebar(sidebarCollapsed)}</div>

      <header className="fixed left-0 right-0 top-0 z-40 flex h-14 items-center justify-between border-b border-border-default bg-surface-elevated px-4 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-content-primary hover:bg-surface-hover"
          aria-label="打开导航"
          aria-controls="mobile-navigation"
          aria-expanded={mobileOpen}
        >
          <RowsIcon className="h-5 w-5" />
        </button>
        <Link to="/" className="font-bold">CultureClick</Link>
        <button
          type="button"
          onClick={openCommand}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-content-primary hover:bg-surface-hover"
          aria-label="搜索"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>
      </header>

      {mobileOpen ? (
        <div className="fixed inset-0 z-[180] lg:hidden">
          <button
            aria-label="关闭导航"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div id="mobile-navigation" className="relative h-full">{renderSidebar(false)}</div>
        </div>
      ) : null}

      <main id="main-content" className={cn('h-full overflow-y-auto pt-14 transition-[padding-left] duration-200 lg:pt-0', sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64')}>
        <Outlet />
      </main>

      {commandOpen ? (
        <div
          className="fixed inset-0 z-[400] flex items-start justify-center pt-[16vh]"
          data-command-panel
          role="dialog"
          aria-modal="true"
          aria-labelledby="command-panel-title"
        >
          <button
            aria-label="关闭搜索"
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            onClick={closeCommand}
          />
          <div className="relative mx-4 w-full max-w-lg overflow-hidden rounded-lg border border-border-default bg-surface-elevated shadow-surface-lg">
            <h2 id="command-panel-title" className="sr-only">搜索页面或功能</h2>
            <div className="flex items-center gap-3 border-b border-border-default px-4 py-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-content-muted" />
              <input
                ref={commandInputRef}
                value={commandQuery}
                onChange={(e) => setCommandQuery(e.target.value)}
                onKeyDown={handleCommandKeyDown}
                placeholder="搜索页面或功能..."
                aria-label="搜索页面或功能"
                aria-activedescendant={commandResults[selectedCommandIndex] ? `command-result-${selectedCommandIndex}` : undefined}
                className="flex-1 bg-transparent text-base text-content-primary outline-none placeholder:text-content-muted"
              />
              <kbd className="rounded-md bg-surface-tertiary px-2 py-1 text-[10px] text-content-muted">ESC</kbd>
            </div>

            <div className="max-h-72 overflow-y-auto p-2" role="listbox">
              {commandResults.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-content-muted">未找到匹配结果</div>
              ) : (
                commandResults.map((result, index) => (
                  <button
                    key={result.label}
                    id={`command-result-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selectedCommandIndex === index}
                    onMouseEnter={() => setSelectedCommandIndex(index)}
                    onClick={() => navigateToCommandItem(result)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition-colors',
                      selectedCommandIndex === index
                        ? 'bg-surface-hover text-content-primary'
                        : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary'
                    )}
                  >
                    <NavIcon icon={result.icon} className="h-4 w-4" />
                    {result.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Layout;
